// @flow
import * as React from 'react';
import * as Immutable from 'immutable';
import isFunction from 'lodash/isFunction';
import EntityImportView from 'common/entity/generic/EntityImportView';
import compose from 'recompose/compose';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import { EntityImportStateChart } from 'common/entity/helpers/EntityImportStateChart';
import { translate } from 'react-i18next';
import {
    AuthenticationActions,
    withAuth,
    type WithAuthPropsType
} from 'common/modules/auth';
import { connect } from 'react-redux';
import { type Dispatch } from 'redux';

import ToastActions from 'common/redux/actions/toast';

import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getPlatformSettings } from 'common/modules/platform-settings';
import { getSelectedBUId } from 'common/redux/selectors/appPreferences';

import FileUtils from 'common/utils/FileUtils';

import { withStatechart } from 'react-automata';
import { matchesState } from 'xstate';
import { withProps } from 'recompose';
import type { TFunction } from 'react-i18next';
import {
    EntityImportTable,
    EntityImportHeader,
    EntityImportRow
} from 'common/models/internal/EntityImportTable';
import ActionButtons from 'common/components/core/actionButtons';
import uuidv4 from 'uuid/v4';
import EntityImportWorkerController from './EntityImportWorkerController';
import EnvConfig from 'config';
import type { TableColumnTypes } from './types';
import type { EntitySettings } from 'common/models/internal/EntitySettings';

const DEFAULT_STATE = {
    fileName: '',
    uploadErrors: null,
    isUploadInProgress: false,
    isUploadFailed: false,
    uploadedTable: null,
    uploadedEntities: null,
    duplicateEntityIds: [],
    ignoredRowIds: [],
    verifiedRowIds: [],
    otherValidationsTableRowIdsList: []
};

const INITIAL_STATE = {
    existingEntitiesTable: null,
    newEntitiesTable: null,
    reservedEntitiesTable: null,
    otherValidationsTable: null,
    showResetConfirmationDialog: false,
    showNonImportableColumns: true,
    tableLastRefreshed: 0,
    entitiesWithErrorsCount: 0
};

type CellMetaTypes = {
    headerId: string,
    rowId: string,
    row: EntityImportRow,
    header: EntityImportHeader,
    rowIndex: number,
    pageNumber: number
};

export type UpdateFilteredColumnsTypes = {
    columns: Array<TableColumnTypes>,
    file: {
        fileRows: Array<Object>,
        fileHeaders: Array<string>
    }
};

type PapaParseConfigType = {
    delimiter?: string
};

type Props = {
    ...WithAuthPropsType,
    uploadedTable: EntityImportTable,
    clientId: string,
    ignoredRowIds: Array<string>,
    fileName?: string,
    editedCells: Array<*>,
    t: TFunction,
    verifiedRowIds: Array<string>,
    defaultColumns: Array<*>,
    templateRowData: Array<*>,
    entityNameId: string,
    machineState: Object,
    transition: (string, ?Object) => void,
    validateEntityIds: () => void,
    addEntities: () => void,
    bulkImport?: boolean,
    sanitizeBeforeUpload?: boolean,
    hasValidationErrors?: Object => boolean,
    hasNonImportableColumns: boolean,
    i18n: Object,
    updateFilteredColumnsAfterUpload?: UpdateFilteredColumnsTypes => Array<TableColumnTypes>,
    isFileBasedEntity: boolean,
    transformEntity: (Array<Object>) => Object,
    isIdAutoGenerated: boolean,
    useIdForValidation?: boolean,
    entityType: string,
    generateEntityList: (Array<*>) => Immutable.List<*>,
    entitiesToUpload: Immutable.List<*>,
    entitiesWithErrors: Immutable.List<*>,
    otherResourcesToFetch: Array<'teams' | 'skills'>,
    requiresUnitConversionForImport: boolean,
    dispatch: Dispatch<$FixMe>,
    doNotAllowNewEntry?: boolean,
    csvParseConfig?: PapaParseConfigType,
    context: Object,
    entitySettings: ?EntitySettings
};

type State = {
    existingEntitiesTable: ?Immutable.List<*>,
    newEntitiesTable: ?Immutable.List<*>,
    reservedEntitiesTable: ?Immutable.List<*>,
    otherValidationsTable: {
        headers: Array<*>,
        rows: {
            [number]: EntityImportTable
        },
        rowsSize: number,
        numberOfPages: number
    },
    showResetConfirmationDialog: boolean,
    entitiesWithErrors: ?Immutable.List<*>,
    entitiesToUpload: ?Immutable.List<*>,
    showNonImportableColumns: boolean,
    tableLastRefreshed: string,
    entitiesWithErrorsCount: number,
    otherValidationsTableRowIdsList: Array<string>
};

export type EntityErrorTypes = {
    nextErrKey: Array<string> | string,
    prevErrKey: Array<string> | string,
    errorMsg: string,
    isAsyncError: boolean,
    cellCoords: Array<?number>
};

const createEntityImportContainer = ({ ImportWorker }) => {
    class EntityImportContainer extends React.PureComponent<Props, State> {
        filteredColumns: Array<TableColumnTypes>;
        mergedHeaders: Array<TableColumnTypes>;
        importWorker: EntityImportWorkerController;
        _pageFetched: number;

        static defaultProps = {
            sanitizeBeforeUpload: false,
            isIdAutoGenerated: false,
            verifiedRowIds: [],
            ignoredRowIds: []
        };

        constructor(props: Props) {
            super(props);
            this.entityErrorIds = Immutable.Map();
            this.state = INITIAL_STATE;
            const filteredColumns = EntityImportHelper.filterOutHiddenColumns(
                props
            );
            this.filteredColumns = filteredColumns;
            const {
                entityNameId,
                clientId,
                isIdAutoGenerated,
                useIdForValidation,
                i18n,
                businessUnitId,
                auth,
                transition,
                doNotAllowNewEntry,
                context
            } = props;
            const { dashboardEndPoint } = EnvConfig;
            this.importWorker = new EntityImportWorkerController({
                ImportWorker
            });
            this.importWorker
                .build({
                    dashboardEndPoint,
                    entityNameId,
                    clientId,
                    isIdAutoGenerated,
                    useIdForValidation,
                    language: i18n?.language,
                    businessUnitId,
                    interceptError: this.interceptWorkerErrors,
                    authHeader: auth.authHeader,
                    doNotAllowNewEntry,
                    context
                })
                .then(() => {
                    transition('REGISTER_SUCCESS');
                });
        }

        onEntityImportReset = () => {
            const { transition } = this.props;
            this.setState(INITIAL_STATE);
            transition('RESET_ENTITY_IMPORT', DEFAULT_STATE);
            this.importWorker.resetImport();
        };

        onResetCancel = () => {
            this.setState({ showResetConfirmationDialog: false });
        };

        onResetClick = () => {
            const { machineState } = this.props;
            if (
                matchesState(
                    machineState.value,
                    'entityProcessing.upload.success'
                )
            ) {
                this.onEntityImportReset();
                return;
            }
            this.setState({ showResetConfirmationDialog: true });
        };

        onUploadTryAgain = () => {
            const { transition } = this.props;
            transition('TRY_AGAIN', DEFAULT_STATE);
        };

        _parseCSV = file => {
            const { csvParseConfig } = this.props;
            this.importWorker
                .parseCSV({ file, config: csvParseConfig })
                .then(this.onFileUploadSuccess)
                .catch(this.onFileUploadError);
        };

        onCSVDrop = (files, rejectedFiles) => {
            const [file] = files;
            if (file) {
                this.onFileUploadStart({ file });
                this._parseCSV(file);
            } else {
                const [rejectedFile] = rejectedFiles;
                this.onFileUploadError({
                    file,
                    uploadErrors: [
                        `Invalid file${
                            rejectedFile ? `: ${rejectedFile.name}` : ''
                        }`
                    ]
                });
            }
        };

        onFileUploadStart = ({ file }) => {
            const { transition } = this.props;
            transition('FILE_UPLOAD_START', {
                isUploadInProgress: true,
                fileName: file.name
            });
        };

        onFileUploadSuccess = ({ fileHeaders }) => {
            const { transition, isIdAutoGenerated } = this.props;
            this.importWorker
                .prepareTableData()
                .then(res => {
                    const { headers } = EntityImportHelper.processHeaders({
                        fileHeaders,
                        defaultColumns: this.filteredColumns,
                        isIdAutoGenerated
                    });
                    this.mergedHeaders = headers;
                    const { hasNonImportableColumns } = res;
                    transition('FILE_UPLOAD_SUCCESS', {
                        isUploadInProgress: false,
                        hasNonImportableColumns
                    });
                })
                .catch(err => {
                    const { uploadErrors } = err;
                    this.onFileUploadError({ uploadErrors });
                });
        };

        onFileUploadError = ({ uploadErrors }) => {
            const { transition } = this.props;
            transition('FILE_UPLOAD_ERROR', {
                uploadErrors,
                isUploadInProgress: false,
                isUploadFailed: true
            });
        };

        validateEntityIds = () => {
            const { transition, doNotAllowNewEntry } = this.props;
            this.importWorker
                .validateEntityIds()
                .then(
                    ({
                        existingEntitiesFound,
                        areFewIdsReserved,
                        areAllIdsReserved,
                        existingEntityIds
                    }) => {
                        if (existingEntitiesFound && !doNotAllowNewEntry) {
                            transition('EXISITING_ENTITIES_FOUND');
                        } else if (areFewIdsReserved) {
                            transition('RESERVED_ENTITIES_FOUND');
                        } else if (areAllIdsReserved) {
                            transition('NO_IDS_TO_UPLOAD');
                        } else {
                            let payload;
                            // hack to bypass existing entities check,
                            // as of now it is only enabled for rider temp upload in liveview
                            if (doNotAllowNewEntry) {
                                payload = {
                                    verifiedRowIds: existingEntityIds
                                };
                            }
                            transition('NO_EXISTING_ENTITIES_FOUND', payload);
                        }
                    }
                )
                .catch(() => {
                    this.onEntityImportReset();
                });
        };

        getIdValidationTables = () => {
            this.importWorker.getIdValidationTables().then(res => {
                const {
                    existingEntitiesTableRows,
                    newEntitiesTableRows,
                    reservedEntitiesTableRows
                } = res;
                this.setState({
                    existingEntitiesTable: EntityImportTable.fromJS({
                        headers: this.mergedHeaders,
                        rows: existingEntitiesTableRows
                    }),
                    newEntitiesTable: EntityImportTable.fromJS({
                        headers: this.mergedHeaders,
                        rows: newEntitiesTableRows
                    }),
                    reservedEntitiesTable: EntityImportTable.fromJS({
                        headers: this.mergedHeaders,
                        rows: reservedEntitiesTableRows
                    })
                });
            });
        };

        _getDataValidationsPage = pageNumber => {
            this.importWorker
                .getNextPage({ pageNumber })
                .then(
                    ({
                        results: { otherValidationsTableRows },
                        total,
                        numberOfPages
                    }) => {
                        this.setState(
                            ({
                                otherValidationsTable,
                                otherValidationsTableRowIdsList
                            }) => {
                                const nextOtherValidationsTable = {
                                    ...otherValidationsTable,
                                    rows: {
                                        ...otherValidationsTable.rows,
                                        [pageNumber]: EntityImportTable.fromJS({
                                            rows: otherValidationsTableRows
                                        })
                                    },
                                    rowsSize: total,
                                    numberOfPages
                                };

                                return {
                                    otherValidationsTable: nextOtherValidationsTable,
                                    otherValidationsTableRowIdsList: otherValidationsTableRowIdsList.concat(
                                        otherValidationsTableRows.map(
                                            ({ id }) => id
                                        )
                                    )
                                };
                            }
                        );
                    }
                );
        };

        fetchDataValidationsPage = pageNumber => {
            if (pageNumber < this._pageFetched) {
                return;
            }
            for (let i = this._pageFetched + 1; i <= pageNumber; i++) {
                setTimeout(() => this._getDataValidationsPage(i));
                this._pageFetched++;
            }
        };

        _generateAndUpdateTableData = (
            {
                results: {
                    otherValidationsTableRows,
                    entityErrorIds,
                    entitiesWithErrorsCount = this.state.entitiesWithErrorsCount
                },
                total,
                numberOfPages
            },
            setStateCallback
        ) => {
            if (entityErrorIds) {
                this.entityErrorIds = Immutable.fromJS(entityErrorIds);
            }
            this._pageFetched = 1;
            this.setState(
                {
                    otherValidationsTable: {
                        headers: this.mergedHeaders,
                        rows: {
                            1: EntityImportTable.fromJS({
                                rows: otherValidationsTableRows
                            })
                        },
                        rowsSize: total,
                        numberOfPages
                    },
                    otherValidationsTableRowIdsList: otherValidationsTableRows.map(
                        ({ id }) => id
                    ),
                    tableLastRefreshed: uuidv4(),
                    entitiesWithErrorsCount
                },
                setStateCallback
            );
        };

        // Entry point of state machine id validation step
        setOtherValidationsTable = () => {
            const {
                ignoredRowIds,
                transition,
                verifiedRowIds,
                doNotAllowNewEntry
            } = this.props;
            this.importWorker
                .getOtherValidationsTableAndSetValidationErrors({
                    ignoredRowIds,
                    verifiedRowIds,
                    doNotAllowNewEntry
                })
                .then(res => {
                    this._generateAndUpdateTableData(res, () => {
                        transition('SHOW_VALIDATION_TABLE');
                    });
                });
        };

        onTableRefresh = (): void => {
            this._pageFetched = 1;
            this.importWorker
                .reorderTableErrorsAndGetData()
                .then(this._generateAndUpdateTableData);
        };

        getTransitions = () => {
            const { transition } = this.props;
            return {
                onSuccess: () => {
                    transition('ENTITY_UPLOAD_SUCCESS');
                },
                onFailure: () => {
                    transition('ENTITY_UPLOAD_ERROR');
                }
            };
        };

        uploadEntities = () => {
            const {
                addEntities,
                clientId,
                verifiedRowIds,
                bulkImport,
                sanitizeBeforeUpload,
                isFileBasedEntity,
                entitiesToUpload: entities
            } = this.props;
            const entitiesToUpload = sanitizeBeforeUpload
                ? entities.map(entity => entity.defaultSanitize())
                : entities;
            const overwrite = verifiedRowIds.length > 0;
            const params = { clientId, overwrite };
            if (bulkImport) {
                params.bulkImport = bulkImport;
            }
            if (isFileBasedEntity) {
                const { transformEntity, addEntity, extraParams } = this.props;
                addEntity(
                    { clientId: params.clientId, ...extraParams },
                    transformEntity(entitiesToUpload),
                    this.getTransitions()
                );
            } else {
                addEntities(params, entitiesToUpload, this.getTransitions());
            }
        };

        onNext = () => {
            const { entitiesToUpload } = this.state;
            this.props.transition('NEXT', {
                noEntitiesToUpload:
                    !entitiesToUpload ||
                    (entitiesToUpload && entitiesToUpload.size === 0)
            });
        };

        onBack = () => {
            this.props.transition('BACK');
        };

        onTryAgain = () => {
            this.props.transition('TRY_AGAIN');
        };

        onDuplicatesSelectionChange = ({ verifiedRowIds, ignoredRowIds }) => {
            const { transition } = this.props;
            const { existingEntitiesTable, newEntitiesTable } = this.state;
            const allIdsMarked =
                existingEntitiesTable.rows.size ===
                verifiedRowIds.length + ignoredRowIds.length;
            const noEntitiesToAddOrUpdate =
                allIdsMarked &&
                verifiedRowIds.length === 0 &&
                newEntitiesTable.rows.size === 0;

            if (noEntitiesToAddOrUpdate) {
                transition('ALL_IDS_IGNORED', {
                    verifiedRowIds,
                    ignoredRowIds
                });
                return;
            }
            if (allIdsMarked) {
                transition('ALL_IDS_MARKED', {
                    verifiedRowIds,
                    ignoredRowIds
                });
                return;
            }

            transition('UPDATE_IDS', {
                verifiedRowIds,
                ignoredRowIds
            });
        };

        /**
         * This method will get called on table column gets blur
         * It will handel value update in modal and state with validations
         * @memberof EntityImportContainer
         */
        onTableEdit = (
            value: string,
            {
                rowId,
                headerId,
                rowIndex,
                pageNumber,
                paginatedRowIndex
            }: CellMetaTypes
        ): void => {
            this.setState(
                prevState => {
                    const {
                        otherValidationsTable: prevOtherValidationsTable
                    } = prevState;
                    const otherValidationsTable = {
                        ...prevOtherValidationsTable,
                        rows: {
                            ...prevOtherValidationsTable.rows,
                            [pageNumber]: prevOtherValidationsTable.rows[
                                pageNumber
                            ].setIn(
                                ['rows', paginatedRowIndex, headerId],
                                value
                            )
                        }
                    };
                    this.entityErrorIds = this.entityErrorIds.setIn(
                        [rowId, headerId, 'isLoading'],
                        true
                    );
                    return { otherValidationsTable };
                },
                () => {
                    this.importWorker
                        .updateTableData({
                            value,
                            cellId: {
                                rowId,
                                headerId,
                                otherValidationTableRowIndex: rowIndex
                            }
                        })
                        .then(({ entityErrorIds, entitiesWithErrorsCount }) => {
                            this.entityErrorIds = Immutable.fromJS(
                                entityErrorIds
                            );
                            this.setState({
                                entitiesWithErrorsCount,
                                tableLastRefreshed: uuidv4()
                            });
                        });
                }
            );
        };

        onDownloadReservedEntities = () => {
            const { fileName, t } = this.props;
            this.importWorker
                .getReservedEntitiesDataForDownload()
                .then(({ headers, rows }) => {
                    FileUtils.createAndDownloadCSV({
                        fileName: `${t('entityImport.removedEntitiesFileName', {
                            originalFileName: fileName.slice(0, -4)
                        })}.csv`,
                        sheetName: t('entityImport.removedEntitiesSheetName'),
                        headers,
                        rows
                    });
                });
        };

        onDownloadIgnoredEntities = () => {
            const { fileName, t } = this.props;
            this.importWorker
                .getIgnoredEntitiesDataForDownload()
                .then(({ headers, rows }) => {
                    FileUtils.createAndDownloadCSV({
                        fileName: `${t('entityImport.ignoredEntitiesFileName', {
                            originalFileName: fileName.slice(0, -4)
                        })}.csv`,
                        sheetName: t('entityImport.ignoredEntitiesSheetName'),
                        headers,
                        rows
                    });
                });
        };

        createAndDownloadTemplate = (templateFileName: string) => {
            const { t, templateRowData, entitySettings } = this.props;

            const defaultRowData = {};
            const rowsForTemplate = [defaultRowData, templateRowData];
            const headerForTemplate = this.filteredColumns.map(column => {
                const key = column.title;
                const isRequired = isFunction(column.required)
                    ? column.required({ entitySettings })
                    : column.required;
                defaultRowData[key] = isRequired
                    ? t('entityImport.mandatory')
                    : t('entityImport.optional');
                return column.title;
            });

            FileUtils.createAndDownloadCSV({
                fileName: templateFileName,
                headers: headerForTemplate,
                rows: rowsForTemplate
            });
        };

        onDownloadTemplate = () => {
            const { t, entityNameId } = this.props;
            const entityName = t(`entities:${entityNameId}`);
            const trimmedEntityName = entityName.split(/\s+/).join('');

            const templateFileName = `${t(
                'entityImport.importTemplateFileName',
                {
                    entityName: trimmedEntityName
                }
            )}.csv`;

            this.createAndDownloadTemplate(templateFileName);
        };

        onDownloadEntitiesWithErrors = () => {
            const { fileName, t } = this.props;
            this.importWorker
                .getErrorEntitiesDataForDownload()
                .then(({ headers, rows }) => {
                    FileUtils.createAndDownloadCSV({
                        fileName: `${t('entityImport.erroredEntitiesFileName', {
                            originalFileName: fileName.slice(0, -4)
                        })}.csv`,
                        sheetName: t('entityImport.erroredEntitiesSheetName'),
                        headers,
                        rows
                    });
                });
        };

        getRowValidationMessage = ({
            entity
        }: {
            entity: Immutable.Map<*, *>
        }): string => {
            const { t } = this.props;
            const { entitiesWithErrors } = this.state;
            if (
                entitiesWithErrors.findIndex(
                    entityWithError => entityWithError.id === entity.id
                ) > -1
            ) {
                return t('entityImport.rowHasErrors');
            }
        };

        interceptWorkerErrors = ({ statusCode, ...rest }) => {
            if (statusCode === 401) {
                const { dispatch } = this.props;
                dispatch(
                    AuthenticationActions.logout({
                        returnTo: window.location.href
                    })
                );
            } else {
                this.showErrorRetryToast(rest);
            }
        };

        showErrorRetryToast = ({
            onRetry,
            retryMetaData,
            message,
            timeout = 0
        }) => {
            const { dispatch, t } = this.props;
            const toastProps = {
                message,
                timeout
            };
            if (onRetry) {
                toastProps.customAction = ({ hideToast }) => {
                    const tryAgain = () => {
                        onRetry(retryMetaData);
                        hideToast();
                    };
                    return (
                        <ActionButtons
                            inverted
                            acceptActionLabel={t('retry')}
                            onAccept={tryAgain}
                            t={t}
                        />
                    );
                };
            }
            dispatch(ToastActions.showErrorToast(toastProps));
        };

        onToggleNonImportableColumns = (
            showNonImportableColumns: boolean
        ): void => {
            this.setState({ showNonImportableColumns });
        };

        getEntityErrorCount = ({ rowId }: { rowId: string }): number => {
            const row = this.entityErrorIds.get(rowId);
            if (row?.some(error => error.get('errorMsg'))) {
                return row.size;
            }
            return 0;
        };

        getTotalErrorCount = (): { rows: number, total: number } => ({
            rows: this.state.entitiesWithErrorsCount,
            total: this.entityErrorIds.get('count')
        });

        updateCellCoordsAndGetError = ({
            rowIndex,
            colIndex,
            rowId,
            headerId
        }: {
            rowIndex: number,
            colIndex: number,
            rowId: string,
            headerId: string
        }): EntityErrorTypes | Object => {
            const error = this.entityErrorIds.getIn([rowId, headerId]);
            if (error?.get('errorMsg')) {
                // update coords for navigating cells
                this.entityErrorIds = this.entityErrorIds.setIn(
                    [rowId, headerId, 'cellCoords'],
                    [rowIndex, colIndex]
                );
                return error.toJS();
            }
            if (error?.get('isLoading')) {
                return {
                    isLoading: true
                };
            }
            return {};
        };

        getErrorByKey = (key: Array<string> | string) => {
            const cellErrorMap = this.entityErrorIds.getIn(key, null);
            const cellError = cellErrorMap?.toJS?.() || cellErrorMap;
            if (
                cellError &&
                'cellCoords' in cellError &&
                cellError.cellCoords[0] === null
            ) {
                // when row is not mounted in virtualized fallback to find rowIndex
                const { otherValidationsTableRowIdsList } = this.state;
                const rowIndex = otherValidationsTableRowIdsList.indexOf(
                    key[0]
                );
                cellError.cellCoords[0] = rowIndex;
            }
            return cellError;
        };

        getUploadSummaryData = () => {
            const { transition, generateEntityList } = this.props;
            this.importWorker
                .getEntitiesToUpload()
                .then(({ entitiesToUpload, entitiesWithErrors }) => {
                    if (entitiesToUpload.length === 0) {
                        transition('NO_UPLOAD', {
                            entitiesWithErrors: generateEntityList(
                                entitiesWithErrors
                            ),
                            entitiesToUpload: Immutable.List()
                        });
                    } else {
                        transition('SUMMARY', {
                            entitiesToUpload: generateEntityList(
                                entitiesToUpload
                            ),
                            entitiesWithErrors: generateEntityList(
                                entitiesWithErrors
                            )
                        });
                    }
                });
        };

        componentWillUnmount() {
            this.importWorker.terminate();
        }

        render() {
            return (
                <EntityImportView
                    {...this.props}
                    {...this.state}
                    getRowValidationMessage={this.getRowValidationMessage}
                    onBack={this.onBack}
                    onNext={this.onNext}
                    onResetImport={this.onEntityImportReset}
                    onResetClick={this.onResetClick}
                    onResetCancel={this.onResetCancel}
                    onUploadTryAgain={this.onUploadTryAgain}
                    onTryAgain={this.onTryAgain}
                    onDuplicatesSelectionChange={
                        this.onDuplicatesSelectionChange
                    }
                    onTableEdit={this.onTableEdit}
                    onDownloadIgnoredEntities={this.onDownloadIgnoredEntities}
                    onDownloadEntitiesWithErrors={
                        this.onDownloadEntitiesWithErrors
                    }
                    onDownloadReservedEntities={this.onDownloadReservedEntities}
                    onDownloadTemplate={this.onDownloadTemplate}
                    onToggleNonImportableColumns={
                        this.onToggleNonImportableColumns
                    }
                    onTableRefresh={this.onTableRefresh}
                    getEntityErrorCount={this.getEntityErrorCount}
                    getTotalErrorCount={this.getTotalErrorCount}
                    updateCellCoordsAndGetError={
                        this.updateCellCoordsAndGetError
                    }
                    getErrorByKey={this.getErrorByKey}
                    onCSVDrop={this.onCSVDrop}
                    fetchDataValidationsPage={this.fetchDataValidationsPage}
                />
            );
        }
    }

    const mapStateToProps = (state, props) => {
        const clientId = clientSelector(state, props);
        const entitySettings = getEntitySettings(state, props);
        const { localeSettings } = getPlatformSettings();
        const businessUnitId = getSelectedBUId(state, props);

        return {
            clientId,
            localeSettings,
            entitySettings,
            businessUnitId
        };
    };

    return compose(
        translate('entityManagement'),
        withProps(() => ({
            initialData: DEFAULT_STATE
        })),
        withAuth,
        connect(mapStateToProps),
        withStatechart(EntityImportStateChart)
    )(EntityImportContainer);
};

export { createEntityImportContainer };
